'use strict';

/**
 * StickerSets
 * -----------
 * The curated, named sticker sets. A "set" is the palette the moodboard / whiteboard gallery shows
 * BEFORE anyone searches; everything else in the three libraries is one search away (see ShapeLibrary
 * / Pict-Provider-ShapesAPI). This file is the single source of truth for the built-in (system) sets
 * and the place we iterate the picks until each one feels right.
 *
 * Each item is { Collection, Name, Title } where Collection is a ShapeLibrary key (phosphor / tabler /
 * simple) and Name is the on-disk icon name. Phosphor carries the two-color duotone look; tabler is a
 * single stroke; simple are brand marks. Every name here renders through ShapeLibrary.getSVG (a build
 * check prunes any that do not), so the gallery never shows a broken tile.
 *
 * Routing (resolved in StickerSetResolver + the app): a board shows the set its drafting kit names,
 * else a per-context default (design boards -> drafting-design, profiles -> moodboard), else the
 * built-in default. Changing or deleting a set never breaks a saved board: a placed sticker carries
 * its own identity + colors on the board node.
 *
 * Five maker themes (laser-cutting, sign-making, hydroponics, press-print, 3d-printing) are sparse in
 * the icon libraries on purpose; they ship as starter sets here and grow with custom uploads.
 */

// Terse constructors so a 16-set file stays readable. Most picks are phosphor.
const P = (pName, pTitle) => ({ Collection: 'phosphor', Name: pName, Title: pTitle });
const T = (pName, pTitle) => ({ Collection: 'tabler', Name: pName, Title: pTitle });
const S = (pName, pTitle) => ({ Collection: 'simple', Name: pName, Title: pTitle });

const _SETS =
{
	'drafting-design':
	{
		Label: 'Drafting & Design',
		Description: 'A design-first starter set: drawing tools, shapes, and the marks you put on a board.',
		Items:
		[
			P('pencil', 'Pencil'), P('pencil-ruler', 'Pencil & ruler'), P('pencil-line', 'Sketch'),
			P('ruler', 'Ruler'), P('compass-tool', 'Compass'), P('eraser', 'Eraser'), P('pen-nib', 'Pen'),
			P('paint-brush', 'Brush'), P('paint-brush-broad', 'Broad brush'), P('paint-bucket', 'Fill'),
			P('paint-roller', 'Roller'), P('palette', 'Palette'), P('swatches', 'Swatches'),
			P('eyedropper', 'Eyedropper'), P('scissors', 'Scissors'), P('crop', 'Crop'),
			P('frame-corners', 'Frame'), P('selection', 'Select'), P('shapes', 'Shapes'),
			P('triangle', 'Triangle'), P('bezier-curve', 'Curve'), P('vector-two', 'Vector'),
			P('note', 'Note'), P('note-pencil', 'Sticky note'), P('notepad', 'Notepad'),
			P('push-pin', 'Pin'), P('paperclip', 'Clip'), P('tag', 'Tag'), P('bookmark-simple', 'Bookmark'),
			P('lightbulb', 'Idea'), P('lightbulb-filament', 'Bright idea'), P('magic-wand', 'Wand'),
			P('sparkle', 'Sparkle'), P('star-four', 'Star'), P('coffee', 'Coffee'), P('plant', 'Plant'),
			P('potted-plant', 'Potted plant'), P('leaf', 'Leaf'), P('flag-banner', 'Flag'),
			P('check-circle', 'Done'), P('list-checks', 'Checklist'), P('clipboard-text', 'Clipboard'),
			P('calendar-blank', 'Calendar'), P('clock', 'Time'), P('chat-circle', 'Comment'),
			P('heart', 'Heart'), P('smiley', 'Smiley')
		]
	},
	'moodboard':
	{
		Label: 'Moodboard',
		Description: 'An expressive set for inspiration and brand boards: feelings, nature, and play.',
		Items:
		[
			P('heart', 'Heart'), P('heart-straight', 'Love'), P('hand-heart', 'With love'), P('star', 'Star'),
			P('star-four', 'Sparkle star'), P('sparkle', 'Sparkle'), P('sun', 'Sun'), P('sun-horizon', 'Sunrise'),
			P('moon-stars', 'Night'), P('rainbow', 'Rainbow'), P('cloud', 'Cloud'), P('flower', 'Flower'),
			P('flower-lotus', 'Lotus'), P('flower-tulip', 'Tulip'), P('leaf', 'Leaf'), P('butterfly', 'Butterfly'),
			P('bird', 'Bird'), P('music-notes', 'Music'), P('camera', 'Camera'), P('image', 'Photo'),
			P('palette', 'Palette'), P('coffee', 'Coffee'), P('gift', 'Gift'), P('balloon', 'Balloon'),
			P('confetti', 'Confetti'), P('cake', 'Cake'), P('crown', 'Crown'), P('trophy', 'Trophy'),
			P('fire', 'Fire'), P('lightning', 'Spark'), P('smiley', 'Smiley'), P('sunglasses', 'Cool'),
			P('martini', 'Drinks'), P('mountains', 'Mountains'), P('umbrella', 'Umbrella'), P('anchor', 'Anchor'),
			P('paw-print', 'Paw'), P('feather', 'Feather'), P('diamond', 'Gem'), P('planet', 'Planet'),
			P('sticker', 'Sticker'), P('bookmark-simple', 'Bookmark')
		]
	},
	'software-system':
	{
		Label: 'Software systems',
		Description: 'Services, data, and the wiring between them.',
		Items:
		[
			P('database', 'Database'), P('cloud', 'Cloud'), P('stack', 'Stack'), P('network', 'Network'),
			P('git-branch', 'Branch'), P('git-merge', 'Merge'), P('gauge', 'Gauge'), P('broadcast', 'Broadcast'),
			P('plugs-connected', 'Connected'), P('circuitry', 'Circuit'), P('hard-drives', 'Storage'),
			P('cpu', 'CPU'), P('tree-structure', 'Tree'), P('flow-arrow', 'Flow'), P('cube', 'Service'),
			S('docker', 'Docker'), S('kubernetes', 'Kubernetes'), S('postgresql', 'PostgreSQL'),
			S('redis', 'Redis'), S('nginx', 'Nginx')
		]
	},
	'software-ui':
	{
		Label: 'Software UI',
		Description: 'Screens, controls, and layout.',
		Items:
		[
			P('cursor', 'Cursor'), P('cursor-click', 'Click'), P('toggle-left', 'Toggle'),
			P('sliders-horizontal', 'Sliders'), P('layout', 'Layout'), P('squares-four', 'Grid'),
			P('sidebar-simple', 'Sidebar'), P('browser', 'Window'), P('browsers', 'Tabs'), P('rows', 'Rows'),
			P('columns', 'Columns'), P('text-aa', 'Text'), P('selection', 'Select'), P('frame-corners', 'Frame'),
			P('app-window', 'App window'), P('radio-button', 'Radio'), S('figma', 'Figma')
		]
	},
	'hardware':
	{
		Label: 'Hardware',
		Description: 'Boards, tools, and the things you plug in.',
		Items:
		[
			P('cpu', 'CPU'), P('memory', 'Memory'), P('wrench', 'Wrench'), P('screwdriver', 'Screwdriver'),
			P('hammer', 'Hammer'), P('plug', 'Plug'), P('plugs-connected', 'Connected'), P('wifi-high', 'Wifi'),
			P('monitor', 'Monitor'), P('devices', 'Devices'), P('battery-high', 'Battery'),
			P('circuitry', 'Circuit'), P('usb', 'USB'), P('gear-six', 'Gear'), P('nut', 'Nut'),
			P('lightning', 'Power'), P('desktop-tower', 'Computer'), P('hard-drives', 'Drive')
		]
	},
	'organization':
	{
		Label: 'Organization',
		Description: 'Sort, file, and keep track.',
		Items:
		[
			P('folder', 'Folder'), P('folder-open', 'Open'), P('folders', 'Folders'), P('archive', 'Archive'),
			P('tray', 'Tray'), P('tag', 'Tag'), P('funnel-simple', 'Filter'), P('kanban', 'Board'),
			P('clipboard-text', 'Clipboard'), P('basket', 'Basket'), P('stack', 'Stack'),
			P('bookmarks-simple', 'Bookmarks'), P('note', 'Note'), P('push-pin', 'Pin'), P('paperclip', 'Clip'),
			P('list-checks', 'Checklist'), P('check-square', 'Check'), P('calendar-blank', 'Calendar')
		]
	},
	'art':
	{
		Label: 'Art & illustration',
		Description: 'Brushes, color, and the studio.',
		Items:
		[
			P('palette', 'Palette'), P('paint-brush', 'Brush'), P('paint-brush-broad', 'Broad brush'),
			P('paint-bucket', 'Fill'), P('pen-nib', 'Pen'), P('swatches', 'Swatches'),
			P('eyedropper', 'Eyedropper'), P('image', 'Image'), P('images', 'Gallery'), P('scissors', 'Scissors'),
			P('crop', 'Crop'), P('frame-corners', 'Frame'), P('pencil', 'Pencil'), P('camera', 'Camera'),
			P('spray-bottle', 'Spray'), S('inkscape', 'Inkscape'), S('gimp', 'GIMP'), S('blender', 'Blender'),
			S('krita', 'Krita')
		]
	},
	'music':
	{
		Label: 'Music',
		Description: 'Notes, instruments, and gear.',
		Items:
		[
			P('music-note', 'Note'), P('music-notes', 'Notes'), P('music-notes-simple', 'Tune'),
			P('guitar', 'Guitar'), P('piano-keys', 'Piano'), P('metronome', 'Metronome'),
			P('microphone', 'Mic'), P('microphone-stage', 'Stage'), P('headphones', 'Headphones'),
			P('speaker-high', 'Speaker'), P('vinyl-record', 'Vinyl'), P('radio', 'Radio'),
			P('playlist', 'Playlist'), P('waveform', 'Waveform')
		]
	},
	'classroom-activity':
	{
		Label: 'Classroom',
		Description: 'Teaching, learning, and the school day.',
		Items:
		[
			P('graduation-cap', 'Graduate'), P('chalkboard', 'Chalkboard'), P('chalkboard-teacher', 'Teacher'),
			P('student', 'Student'), P('backpack', 'Backpack'), P('book', 'Book'), P('book-open', 'Open book'),
			P('pencil', 'Pencil'), P('presentation', 'Presentation'), P('puzzle-piece', 'Puzzle'),
			P('ruler', 'Ruler'), P('notebook', 'Notebook'), P('exam', 'Exam')
		]
	},
	'gardening':
	{
		Label: 'Gardening',
		Description: 'Plants, beds, and the tools to tend them.',
		Items:
		[
			P('plant', 'Plant'), P('potted-plant', 'Potted plant'), P('tree', 'Tree'),
			P('tree-evergreen', 'Evergreen'), P('flower', 'Flower'), P('flower-tulip', 'Tulip'),
			P('leaf', 'Leaf'), P('shovel', 'Shovel'), P('drop', 'Water'), P('sun-horizon', 'Sun'),
			P('thermometer', 'Temperature'), P('carrot', 'Carrot'), P('cactus', 'Cactus'),
			T('garden-cart', 'Cart'), T('plant-2', 'Sprout')
		]
	},
	'hydroponics':
	{
		Label: 'Hydroponics',
		Description: 'A starter set for water-grown systems. Add your own grow lights and net pots.',
		Items:
		[
			P('drop', 'Water'), P('drop-half-bottom', 'Reservoir'), P('flow-arrow', 'Flow'),
			P('test-tube', 'Test tube'), P('flask', 'Nutrients'), P('thermometer', 'Temperature'),
			P('plant', 'Plant'), P('sun-horizon', 'Light'), P('gauge', 'pH'), T('plant-2', 'Sprout')
		]
	},
	'laser-cutting':
	{
		Label: 'Laser cutting',
		Description: 'A starter set. The maker-specific marks (laser head, kerf, bed) come from uploads.',
		Items:
		[
			P('crosshair', 'Focus'), P('lightning', 'Beam'), P('scan', 'Engrave'), P('ruler', 'Ruler'),
			P('cube', 'Material'), P('frame-corners', 'Bed'), P('grid-four', 'Grid'), P('scissors', 'Cut'),
			T('vector', 'Vector'), T('dimensions', 'Dimensions')
		]
	},
	'sign-making':
	{
		Label: 'Sign making',
		Description: 'A starter set. Add vinyl cutter, weeding tools, and transfer tape as uploads.',
		Items:
		[
			P('text-aa', 'Lettering'), P('swatches', 'Swatches'), P('ruler', 'Ruler'), P('scissors', 'Cut'),
			P('frame-corners', 'Layout'), P('paint-roller', 'Apply'), T('typography', 'Type'),
			T('signature', 'Sign'), T('vinyl', 'Vinyl')
		]
	},
	'press-print':
	{
		Label: 'Press & print',
		Description: 'A starter set. Add screen, squeegee, and registration marks as uploads.',
		Items:
		[
			P('printer', 'Printer'), P('newspaper', 'Press'), P('stamp', 'Stamp'),
			P('paint-roller', 'Ink roller'), P('stack', 'Stack'), P('copy', 'Copies'), P('swatches', 'Swatches')
		]
	},
	'3d-printing':
	{
		Label: '3D printing',
		Description: 'A starter set. Add printer, filament spool, and nozzle as uploads.',
		Items:
		[
			P('cube', 'Model'), P('cube-transparent', 'Mesh'), P('cube-focus', 'Slice'), P('polygon', 'Polygon'),
			P('sphere', 'Sphere'), T('cone', 'Cone'), P('scan', 'Scan'), P('stack', 'Layers'),
			P('ruler', 'Measure'), T('cube-3d-sphere', '3D'), T('axis-x', 'X axis'), T('axis-y', 'Y axis')
		]
	}
};

// Display order for the picker / preview, and the routing keys the resolver + app rely on.
const SET_ORDER =
[
	'drafting-design', 'moodboard', 'software-system', 'software-ui', 'hardware', 'organization',
	'art', 'music', 'classroom-activity', 'gardening', 'hydroponics', 'laser-cutting', 'sign-making',
	'press-print', '3d-printing'
];

// The built-in fallback (design boards) and the profile/brand fallback.
const DEFAULT_SET_KEY = 'drafting-design';
const PROFILE_SET_KEY = 'moodboard';

module.exports = { Sets: _SETS, SET_ORDER, DEFAULT_SET_KEY, PROFILE_SET_KEY };
